import { useState, useRef, useEffect } from 'react'

interface UseMusicTrackDraggingProps {
  musicStartTime: number
  audioDuration: number
  videoDuration: number
  timelineDuration: number
  onMusicStartTimeChange: (time: number) => void
  onPausePlayback?: () => void
  onResumePlayback?: () => void
  onTimeChange?: (time: number) => void
  isPlaying?: boolean
}

interface UseMusicTrackDraggingReturn {
  isDraggingMusic: boolean
  handleMusicDragStart: (e: React.MouseEvent<HTMLDivElement>) => void
  musicActiveLeft: number
  musicActiveWidth: number
}

export const useMusicTrackDragging = ({
  musicStartTime,
  audioDuration,
  videoDuration,
  timelineDuration,
  onMusicStartTimeChange,
  onPausePlayback,
  onResumePlayback,
  onTimeChange,
  isPlaying = false,
}: UseMusicTrackDraggingProps): UseMusicTrackDraggingReturn => {
  const [isDraggingMusic, setIsDraggingMusic] = useState(false)
  const dragStartX = useRef<number>(0)
  const dragStartTime = useRef<number>(0)
  const trackWidthRef = useRef<number>(0)
  const wasPlayingBeforeDrag = useRef<boolean>(false)

  const handleMusicDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    // Remember if video was playing before we pause it
    wasPlayingBeforeDrag.current = isPlaying

    // Pause video and seek to 0 when starting to drag
    onPausePlayback?.()
    onTimeChange?.(0)

    setIsDraggingMusic(true)
    dragStartX.current = e.clientX
    dragStartTime.current = musicStartTime

    const audioBlock = e.currentTarget
    const trackFullRow = audioBlock.parentElement
    if (trackFullRow) {
      trackWidthRef.current = trackFullRow.offsetWidth
    }
  }

  useEffect(() => {
    if (!isDraggingMusic) return

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - dragStartX.current
      const deltaTime = (deltaX / trackWidthRef.current) * timelineDuration
      const newStartTime = dragStartTime.current + deltaTime

      const minStartTime = -(audioDuration - 1)
      const maxStartTime = videoDuration - 1
      const constrainedStartTime = Math.max(minStartTime, Math.min(maxStartTime, newStartTime))

      onMusicStartTimeChange(constrainedStartTime)
    }

    const handleMouseUp = () => {
      setIsDraggingMusic(false)
      // Reset video to start
      onTimeChange?.(0)
      // Only resume playback if it was playing before dragging started
      if (wasPlayingBeforeDrag.current) {
        onResumePlayback?.()
      }
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [
    isDraggingMusic,
    timelineDuration,
    videoDuration,
    audioDuration,
    onMusicStartTimeChange,
    onTimeChange,
    onResumePlayback,
    isPlaying,
  ])

  const musicActiveLeft = Math.round((musicStartTime / timelineDuration) * 100)
  const musicActiveWidth = Math.round((audioDuration / timelineDuration) * 100)

  return {
    isDraggingMusic,
    handleMusicDragStart,
    musicActiveLeft,
    musicActiveWidth,
  }
}
